import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SeoService } from '../../core/services/seo';
import { ApiService } from '../../core/services/api';
import { RouterLink } from '@angular/router';
import { EventCardComponent } from '../../shared/components/event-card/event-card';
import { CustomTourModalComponent } from '../../shared/components/custom-tour-modal/custom-tour-modal';
import { EventBookingModalComponent } from '../../shared/components/event-booking-modal/event-booking-modal';
import { MapComponent } from '../../shared/components/map/map';
import { EventCardSkeletonComponent } from '../../shared/components/skeletons/event-card-skeleton';


@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, TranslateModule, RouterLink, EventCardComponent, CustomTourModalComponent, EventBookingModalComponent, MapComponent, EventCardSkeletonComponent],
  template: `
    <!-- Hero Section -->
    <section class="relative min-h-[100dvh] flex flex-col items-center pb-20 text-center text-white bg-enduro-black">
      <!-- Background Gradient (Dark theme) -->
      <div class="absolute inset-0 bg-gradient-to-b from-gray-900 to-enduro-black"></div>

      <!-- Main Content Container -->
      <div class="relative z-10 w-full max-w-4xl mx-auto flex flex-col items-center justify-center flex-grow px-4 py-8 md:py-12 gap-8 md:gap-12">
        
        <!-- Top Content: Logo & Text -->
        <div class="flex flex-col items-center justify-center">
            <img src="assets/logo.png" alt="Tour Enduro Logo" class="w-24 md:w-40 h-auto mb-6 drop-shadow-lg">
            <h1 class="text-3xl md:text-5xl font-bold mb-4 md:mb-6 tracking-tighter uppercase leading-tight">
            {{ 'HOME.TITLE' | translate }}
            </h1>
            <p class="text-lg md:text-xl text-gray-200 max-w-2xl leading-relaxed">
            {{ 'HOME.SUBTITLE' | translate }}
            </p>
        </div>

        <!-- Bottom Content: Buttons -->
        <div class="flex flex-col w-full sm:w-auto sm:flex-row gap-4 justify-center">
          <a routerLink="/membership" class="w-full sm:w-auto bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-bold uppercase tracking-wide text-sm md:text-base hover:bg-white hover:text-black transition-colors text-center">
            {{ 'HOME.CTA_MEMBERSHIP' | translate }}
          </a>
          <button (click)="openCustomTourModal()" class="w-full sm:w-auto bg-enduro-orange text-white px-8 py-3 rounded-full font-bold uppercase tracking-wide text-sm md:text-base hover:bg-opacity-90 transition-colors shadow-lg hover:shadow-orange-500/30">
            {{ 'EVENTS.CUSTOM_TOUR.BUTTON' | translate }}
          </button>
        </div>
      </div>
    </section>
    
    <!-- Map Section -->
    <section class="hidden md:block py-16 bg-white">
        <div class="container mx-auto px-4">
            <div class="text-center mb-8 md:mb-12">
                <h2 class="text-2xl md:text-3xl font-bold mb-4 uppercase text-enduro-black">{{ 'MAP.TITLE' | translate }}</h2>
                <div class="w-20 h-1 bg-enduro-orange mx-auto mb-6"></div>
                <p class="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
                    {{ 'MAP.DESCRIPTION' | translate }}
                </p>
            </div>
            
            
            <!-- Map -->
            <div class="rounded-xl overflow-hidden shadow-lg border-2 border-gray-100">
                    <app-map></app-map>
                </div>

        </div>
    </section>

    <!-- Content Sections -->
    <section class="py-12 md:py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-8 md:mb-12">
          <h2 class="text-2xl md:text-3xl font-bold mb-4 uppercase text-enduro-black">{{ 'HOME.NEXT_EVENTS' | translate }}</h2>
          <div class="w-20 h-1 bg-enduro-orange mx-auto"></div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          @if (isLoading()) {
            <app-event-card-skeleton></app-event-card-skeleton>
            <app-event-card-skeleton></app-event-card-skeleton>
            <app-event-card-skeleton></app-event-card-skeleton>
          } @else {
            @for (event of events(); track event.id) {
              <app-event-card [event]="event" (book)="openBookingModal($event)"></app-event-card>
            }
          }
        </div>
      </div>
    </section>

    <!-- Custom Tour Modal -->
    @if (showCustomTourModal()) {
      <app-custom-tour-modal-v2 (closed)="closeCustomTourModal()" />
    }

    <!-- Booking Modal -->
    @if (bookingEvent()) {
      <app-event-booking-modal 
        [event]="bookingEvent()" 
        (closed)="closeBookingModal()"
        (booked)="onBooked()"
      ></app-event-booking-modal>
    }
  `
})
export class HomeComponent implements OnInit {
  private seo = inject(SeoService);

  ngOnInit() {
    this.seo.setJsonLd({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Tour Enduro Marghine",
      "url": "https://enduromarghine.it",
      "logo": "https://enduromarghine.it/assets/logo.png",
      "description": "Tour Enduro guidati ed eventi motoristici in Sardegna, nel cuore del Marghine.",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+39 349 625 3021",
        "contactType": "customer service"
      }
    });
  }
  api = inject(ApiService);
  events = signal<any[]>([]);
  isLoading = signal(true);
  showCustomTourModal = signal(false);
  bookingEvent = signal<any>(null);

  constructor() {
    this.loadEvents();
  }

  loadEvents() {
    this.isLoading.set(true);
    this.api.get<any[]>('events').subscribe({
      next: (data) => {
        this.events.set(data.slice(0, 3)); // Show top 3
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.isLoading.set(false);
      }
    });
  }

  openCustomTourModal() {
    this.showCustomTourModal.set(true);
  }

  closeCustomTourModal() {
    this.showCustomTourModal.set(false);
  }

  openBookingModal(event: any) {
    this.bookingEvent.set(event);
  }

  closeBookingModal() {
    this.bookingEvent.set(null);
  }

  onBooked() {
    this.loadEvents(); // Refresh participants count
  }
}
